/**
 * AtomSource — modulation source whose value sequence comes from an
 * element's electron shell occupancy.
 *
 * Behaves like Sample-and-Hold: a setTimeout-driven clock advances
 * through the current element's shells; each step writes a normalized
 * [-1, +1] value to a `ConstantSourceNode.offset` that the ModRouter
 * routes to wherever the user wires this source.
 *
 * Three knobs of behavior:
 *
 *   - **Element**: which row of the periodic table provides the
 *     sequence. H is constant (one shell). Au sweeps through six
 *     non-monotonic values. Heavier elements have richer paths.
 *   - **Rate**: how quickly the source steps through the shells.
 *     Free-run Hz, no transport sync (could be added later by
 *     swapping in a Transport-aware ticker like the Sequencer uses).
 *   - **Scale + Raw**: govern how shells map to musical pitches.
 *     They drive both the mod-output normalization (so `Atom →
 *     Cutoff` walks musical intervals when scale-quantized) and the
 *     `toChordNotes()` output the panel uses for the Play button.
 *
 * The source is also a `SynthModule` so it round-trips through the
 * existing serialize/deserialize / preset capture machinery without
 * special-casing.
 */

import { NumericParameter, EnumParameter, type AnyParameter } from '../../params/Parameter';
import type { AudioEngine } from '../AudioEngine';
import type { ModuleState, SynthModule } from '../modules/SynthModule';
import { mintId, readParamState, writeParamState } from '../modules/util';
import { SYNC_MODES, type SyncMode, type Transport } from '../Transport';
import {
  ELEMENTS_BY_SYMBOL,
  ATOM_SCALES,
  ATOM_SCALE_NAMES,
  type Element,
  type AtomScaleName,
  shellToMidi,
  elementToChord,
} from '../elements';

const DEFAULT_ELEMENT = 'Au'; // Gold — six shells, evocative chord
const DEFAULT_ROOT = 48; // C3
const MIN_RATE_HZ = 0.1;
const MAX_RATE_HZ = 30;

export type AtomListener = (source: AtomSource) => void;

export class AtomSource implements SynthModule {
  readonly kind = 'modulation' as const;
  readonly type = 'atom' as const;
  readonly displayName = 'ATOM';
  readonly id: string;
  bypass = false;

  // ── Parameters (preset-round-trippable) ────────────────────────────
  // Element symbol stored as an EnumParameter so the preset path
  // captures it cleanly. Options list is the full periodic table.
  readonly element: EnumParameter<string>;
  readonly rate = new NumericParameter({
    name: 'Rate',
    default: 4,
    min: MIN_RATE_HZ,
    max: MAX_RATE_HZ,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 0,
    formatter: (v) => `${v.toFixed(2)}Hz`,
  });
  readonly root = new NumericParameter({
    name: 'Root',
    default: DEFAULT_ROOT,
    min: 24,
    max: 96,
    curve: 'linear',
    unit: 'midi',
    smoothingMs: 0,
    randomizable: false,
    formatter: (v) => midiToNoteName(Math.round(v)),
  });
  readonly scale: EnumParameter<AtomScaleName>;
  readonly raw = new EnumParameter<'off' | 'on'>({
    name: 'Raw',
    default: 'off',
    options: ['off', 'on'],
    randomizable: false,
    formatter: (v) => (v === 'on' ? 'RAW' : 'SCALE'),
  });
  /**
   * How keyboard input is handled while the user plays. 'off' is
   * normal — keys play single notes. 'chord' transposes the element's
   * shell-chord by the pressed key (Carbon-on-C, Carbon-on-G…). 'arp'
   * cycles shell notes one at a time at the same `rate` the mod
   * source ticks at, while the key is held.
   *
   * Lives on the source rather than the panel so it round-trips
   * through preset capture and the panel can render it without
   * duplicating state.
   */
  readonly trigger = new EnumParameter<'off' | 'chord' | 'arp'>({
    name: 'Trigger',
    default: 'off',
    options: ['off', 'chord', 'arp'],
    randomizable: false,
    formatter: (v) => v,
  });
  /**
   * Tempo-sync setting. 'free' uses `rate` Hz directly. Any musical
   * division ('1/4', '1/8', '1/16T', etc) ignores `rate` and steps
   * at that division of the transport's BPM. Mirrors LFOModule.sync
   * so the UX is consistent across modulators.
   */
  readonly sync = new EnumParameter<SyncMode>({
    name: 'Sync',
    default: 'free',
    options: [...SYNC_MODES],
    randomizable: false,
    formatter: (v) => v,
  });

  readonly parameters: readonly AnyParameter[];

  // ── Audio output ───────────────────────────────────────────────────
  private constantSource: ConstantSourceNode | null = null;
  private engine: AudioEngine | null = null;
  // Step counter — index into the current element's shells array.
  private stepIndex = 0;
  // setTimeout handle for the clock. Not sample-accurate; matches S&H.
  private timer: ReturnType<typeof setTimeout> | null = null;
  // Listeners called whenever the step advances OR the element/scale
  // change. UI uses this to highlight the current shell + redraw.
  private readonly listeners = new Set<AtomListener>();
  private started = false;

  /** Transport used to compute interval when sync != 'free'. Optional —
   *  `undefined` falls back to free-run Hz regardless of sync setting. */
  private readonly transport: Transport | null;

  constructor(id?: string, transport?: Transport) {
    this.id = id ?? mintId('atom');
    this.transport = transport ?? null;

    // Element options — use the canonical periodic table list (118).
    const elementSymbols = [...ELEMENTS_BY_SYMBOL.keys()];
    this.element = new EnumParameter<string>({
      name: 'Element',
      default: DEFAULT_ELEMENT,
      options: elementSymbols,
      // Randomize would reroll the user's chosen element on every
      // Ctrl+R, which is more surprising than helpful. Keep this
      // tied to the panel's grid so element selection is intentional.
      randomizable: false,
      formatter: (v) => v,
    });

    this.scale = new EnumParameter<AtomScaleName>({
      name: 'Scale',
      default: 'minor-pentatonic',
      options: [...ATOM_SCALE_NAMES],
      randomizable: false,
      formatter: (v) => v,
    });

    this.parameters = [this.element, this.rate, this.root, this.scale, this.raw, this.trigger, this.sync];

    // Re-render listeners + restart the clock when params change so
    // edits during playback take effect immediately.
    this.element.subscribe(() => {
      this.stepIndex = 0;
      this.emit();
    });
    this.scale.subscribe(() => this.emit());
    this.raw.subscribe(() => this.emit());
    this.root.subscribe(() => this.emit());
    this.trigger.subscribe(() => this.emit());
    const restartClock = (): void => {
      if (this.started) {
        this.stopClock();
        this.startClock();
      }
    };
    this.rate.subscribe(restartClock);
    this.sync.subscribe(restartClock);
    // BPM changes mid-sync should re-pace the clock too. Hot-reload
    // intervals when the tempo moves so a tempo change hits on the
    // very next tick instead of waiting for the current interval.
    if (this.transport) {
      this.transport.bpm.subscribe(() => {
        if (this.sync.value !== 'free') restartClock();
      });
    }
  }

  /** AudioNode the ModRouter wires to depth-gain → target params. */
  get modOutput(): AudioNode | null {
    return this.constantSource;
  }

  /** Modulation modules have no audio I/O. */
  readonly input: AudioNode | null = null;
  readonly output: AudioNode | null = null;

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;
    this.constantSource = ctx.createConstantSource();
    this.constantSource.offset.value = 0;
    this.constantSource.start();
    this.started = true;
    this.startClock();
  }

  dispose(): void {
    this.stopClock();
    if (this.constantSource) {
      try {
        this.constantSource.stop();
      } catch {
        /* already stopped */
      }
      this.constantSource.disconnect();
      this.constantSource = null;
    }
    this.engine = null;
    this.listeners.clear();
    this.started = false;
  }

  /** Subscribe to step changes. Fires immediately with the current
   *  step so late subscribers don't miss it. Returns unsubscribe. */
  subscribe(fn: AtomListener): () => void {
    this.listeners.add(fn);
    fn(this);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Index of the shell currently driving the mod output. */
  getCurrentStepIndex(): number {
    return this.stepIndex;
  }

  /** The element currently selected — convenience for the UI. */
  getElement(): Element {
    return ELEMENTS_BY_SYMBOL.get(this.element.value) ?? ELEMENTS_BY_SYMBOL.get(DEFAULT_ELEMENT)!;
  }

  /**
   * Convert the current element's shells to MIDI notes given the
   * current root/scale/raw settings. Used by the Play Chord button
   * — fires `voices.noteOn(midi, velocity)` for each note in the
   * returned array.
   */
  toChordNotes(): number[] {
    const el = this.getElement();
    return elementToChord(el, this.root.value, this.scale.value, this.raw.value === 'on');
  }

  // ── Module serialization ──────────────────────────────────────────

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }

  // ── Internal: clock + value computation ───────────────────────────

  private startClock(): void {
    if (!this.engine || !this.constantSource) return;
    this.stopClock();
    // Apply the current step's value immediately so the source isn't
    // silently waiting one tick before producing output.
    this.applyStep();
    this.scheduleNext();
  }

  private stopClock(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Compute the next-tick interval honoring sync mode. 'free' uses
   *  the rate parameter directly; any musical division uses the
   *  transport's BPM resolver so the source steps lock to tempo. */
  private currentIntervalMs(): number {
    const sync = this.sync.value;
    if (sync !== 'free' && this.transport) {
      return 1000 * this.transport.divisionToSeconds(sync);
    }
    return 1000 / Math.max(MIN_RATE_HZ, this.rate.value);
  }

  private scheduleNext(): void {
    if (!this.started) return;
    this.timer = setTimeout(() => {
      this.advance();
      this.scheduleNext();
    }, this.currentIntervalMs());
  }

  /**
   * Public helper for consumers that want to schedule something on
   * the same tempo grid as the source — e.g. AtomPanel's keyboard
   * arp piggybacks on the source's clock by reading this so the
   * shells visualization and the arp's note-firing stay aligned.
   */
  getCurrentIntervalMs(): number {
    return this.currentIntervalMs();
  }

  private advance(): void {
    const el = this.getElement();
    const shellCount = el.shells.length;
    if (shellCount === 0) return;
    this.stepIndex = (this.stepIndex + 1) % shellCount;
    this.applyStep();
    this.emit();
  }

  /**
   * Compute the current step's normalized [-1, +1] mod value and
   * write it to the ConstantSourceNode offset.
   *
   * Scale-quantized output: convert the shell to a MIDI note via
   * the same shellToMidi the chord button uses, then normalize the
   * note relative to the element's overall pitch range. That keeps
   * the mod output musically aligned with what the user hears when
   * they hit Play Chord.
   *
   * Raw output: normalize each shell relative to the element's own
   * shell-value range so single-shell elements (H) sit at 0 and
   * any multi-shell element exploits the full ±1 range.
   */
  private applyStep(): void {
    if (!this.engine || !this.constantSource) return;
    const el = this.getElement();
    const shells = el.shells;
    const shell = shells[this.stepIndex] ?? 0;

    const raw = this.raw.value === 'on';
    const root = this.root.value;
    const scaleName = this.scale.value;

    // Compute MIDI for each shell so we can normalize THIS step
    // against the element's range. Cheap — shells.length ≤ 7.
    const allMidi = shells.map((s) => shellToMidi(s, root, scaleName, raw));
    const minMidi = Math.min(...allMidi);
    const maxMidi = Math.max(...allMidi);
    const stepMidi = shellToMidi(shell, root, scaleName, raw);

    let value = 0;
    if (maxMidi !== minMidi) {
      // Linear-map [min, max] → [-1, +1].
      value = ((stepMidi - minMidi) / (maxMidi - minMidi)) * 2 - 1;
    }

    const t = this.engine.context.currentTime;
    this.constantSource.offset.cancelScheduledValues(t);
    this.constantSource.offset.setValueAtTime(value, t);
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this);
  }
}

/** Tiny MIDI-number → note-name formatter, used by the Root parameter. */
function midiToNoteName(midi: number): string {
  const NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
  const n = NAMES[midi % 12] ?? '?';
  const octave = Math.floor(midi / 12) - 1;
  return `${n}${octave}`;
}

// Re-export the scale name list so consumers don't have to reach
// into the elements module separately when wiring an AtomPanel.
export { ATOM_SCALE_NAMES, ATOM_SCALES, type AtomScaleName };
